import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, GraduationCap, Award, Users, BookOpen } from 'lucide-react';
import { heroSlides, stats, pillars, events, galleryImages, ASSET } from '../mock/data';

const Hero = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % heroSlides.length), 5000);
    return () => clearInterval(t);
  }, []);
  const slide = heroSlides[idx];

  return (
    <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="fade-up">
            <div className="flex items-center gap-3 text-refocc-orange text-sm font-medium tracking-wider uppercase mb-6">
              <span className="w-10 h-px bg-refocc-orange"></span>
              <span>Since 2013 — Kerala to the world</span>
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-refocc-dark">
              A community woven by <em className="italic text-refocc-orange font-normal">compassion,</em> culture & collective action.
            </h1>
            <p className="mt-8 text-lg text-refocc-dark/70 max-w-xl leading-relaxed">REFOCC — Regional Foundation For Charity & Cultural Development — is a growing community of volunteers, families, and well-wishers committed to creating meaningful social change. From Mavoor, Kerala, to wider global connections, REFOCC brings people together to serve, empower, and celebrate communities with dignity.</p>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="http://scholarships.refocc.org" className="inline-flex items-center gap-2 bg-refocc-orange hover:bg-[#B97651] text-white px-6 py-3.5 rounded-full font-medium transition-colors">Scholarship - Apply Now <ArrowRight size={16} /></Link>
              <Link to="/events" className="inline-flex items-center gap-2 border border-refocc-dark/25 text-refocc-dark px-6 py-3.5 rounded-full font-medium hover:bg-refocc-dark/5 transition-colors">Upcoming Events</Link>
            </div>

            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6">
              {stats.map(s => (
                <div key={s.label}>
                  <div className="font-serif text-3xl md:text-4xl text-refocc-dark">{s.num}</div>
                  <div className="text-xs text-refocc-dark/60 mt-1 leading-snug">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="relative aspect-[4/5] w-full rounded-[2rem] overflow-hidden bg-refocc-dark/10 shadow-2xl">
              {heroSlides.map((s, i) => (
                <img key={i} src={s.img} alt={s.title} loading={i === 0 ? 'eager' : 'lazy'}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === idx ? 'opacity-100' : 'opacity-0'}`} />
              ))}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-6 pt-20">
                <div className="text-[10px] tracking-[0.18em] text-white/80 uppercase">{slide.kicker}</div>
                <div className="text-white font-serif text-2xl mt-1 max-w-md">{slide.title}</div>
              </div>
              <div className="absolute top-5 right-5 flex gap-1.5">
                {heroSlides.map((_, i) => (
                  <button key={i} onClick={() => setIdx(i)} aria-label={`slide ${i + 1}`}
                    className={`h-1 rounded-full transition-all ${i === idx ? 'w-8 bg-white' : 'w-4 bg-white/50'}`}></button>
                ))}
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 lg:-left-10 bg-white shadow-xl rounded-2xl p-4 max-w-[240px] hidden sm:block">
              <div className="text-[10px] tracking-[0.18em] text-refocc-dark/50 uppercase">{slide.tag}</div>
              <div className="font-serif text-refocc-dark text-lg leading-snug mt-1">{slide.sub}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const About = () => (
  <section className="py-24 lg:py-32 bg-refocc-cream">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center">
      <div className="relative">
        <img src="/assets/group-photo.jpg" alt="REFOCC team" className="rounded-[2rem] w-full aspect-[4/3] object-cover shadow-xl" />
        <div className="absolute -top-5 -right-5 bg-refocc-orange text-white font-serif text-sm px-5 py-3 rounded-full shadow-lg hidden sm:block">A community of changemakers</div>
      </div>
      <div>
        <div className="text-refocc-orange uppercase tracking-[0.2em] text-xs mb-4">About REFOCC</div>
        <h2 className="font-serif text-4xl md:text-5xl text-refocc-dark leading-tight">A community that chooses <em className="italic text-refocc-orange font-normal">each other,</em> again and again.</h2>
        <p className="mt-6 text-refocc-dark/70 leading-relaxed">REFOCC is built by people from different backgrounds and walks of life, united by a shared belief: when goodwill is organised, it can become a powerful force for change. The organization works to create spaces where compassion, unity, and empowerment are not ideals alone, but everyday practices.</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/about" className="inline-flex items-center gap-2 bg-refocc-dark-mid text-refocc-cream px-6 py-3 rounded-full font-medium hover:bg-[#2f404b] transition-colors">Read our story <ArrowRight size={16} /></Link>
          <Link to="/gallery" className="inline-flex items-center gap-2 border border-refocc-dark/25 text-refocc-dark px-6 py-3 rounded-full font-medium hover:bg-refocc-dark/5 transition-colors">Browse Gallery</Link>
        </div>
      </div>
    </div>
  </section>
);

const Pillars = () => (
  <section className="py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
        <div className="max-w-2xl">
          <div className="text-refocc-orange uppercase tracking-[0.2em] text-xs mb-4">What we do</div>
          <h2 className="font-serif text-4xl md:text-5xl text-refocc-dark leading-tight">Four pillars. One shared <em className="italic text-refocc-orange font-normal">purpose.</em></h2>
        </div>
        <p className="text-refocc-dark/70 max-w-md">Every programme is a conversation with the community — shaped by their needs, led by their voices.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {pillars.map(p => (
          <Link to={p.href} key={p.title} className="group bg-white rounded-3xl overflow-hidden border border-refocc-dark/10 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
            <div className="aspect-[4/3] overflow-hidden">
              <img src={p.img} alt={p.title} className="w-full h-full object-cover img-hover" />
            </div>
            <div className="p-6">
              <h3 className="font-serif text-xl text-refocc-dark">{p.title}</h3>
              <p className="text-sm text-refocc-dark/65 mt-2 leading-relaxed">{p.desc}</p>
              <div className="mt-4 inline-flex items-center gap-1.5 text-refocc-orange text-sm font-medium group-hover:gap-2.5 transition-all">Explore <ArrowRight size={14} /></div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const EventsPreview = () => (
  <section className="py-24 bg-refocc-cream">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
        <div>
          <div className="text-refocc-orange uppercase tracking-[0.2em] text-xs mb-4">Upcoming & Flagship</div>
          <h2 className="font-serif text-4xl md:text-5xl text-refocc-dark leading-tight">Events that bring us <em className="italic text-refocc-orange font-normal">together.</em></h2>
        </div>
        <Link to="/events" className="inline-flex items-center gap-2 text-refocc-dark font-medium hover:text-refocc-orange">View all events <ArrowUpRight size={16} /></Link>
      </div>
      <p className="text-refocc-dark/70 max-w-2xl mb-10">From flagship summits to local gatherings, REFOCC events bring the community together to learn, collaborate, celebrate, and serve.</p>
      <div className="grid md:grid-cols-3 gap-6">
        {events.slice(0, 3).map((e, i) => (
          <div key={e.title} className={`bg-white rounded-3xl overflow-hidden border border-refocc-dark/10 hover:shadow-xl transition-all duration-300 ${i === 0 ? 'md:row-span-2' : ''}`}>
            <div className={`overflow-hidden ${i === 0 ? 'aspect-[4/5]' : 'aspect-[4/3]'}`}>
              <img src={e.img} alt={e.title} className="w-full h-full object-cover img-hover" />
            </div>
            <div className="p-6">
              <div className="text-[10px] uppercase tracking-[0.18em] text-refocc-orange mb-2">{e.tag}</div>
              <h3 className="font-serif text-xl text-refocc-dark leading-snug">{e.title}</h3>
              <p className="text-sm text-refocc-dark/60 mt-2">{e.when}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const DyuthiSection = () => (
  <section className="py-24 bg-gradient-to-br from-refocc-dark via-[#2a3b45] to-refocc-dark text-refocc-cream relative overflow-hidden">
    <div className="absolute top-0 right-0 w-96 h-96 bg-refocc-orange/10 rounded-full blur-[120px]"></div>
    <div className="absolute bottom-0 left-0 w-80 h-80 bg-refocc-orange/5 rounded-full blur-[100px]"></div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
      <div className="text-center mb-16">
        <div className="inline-flex items-center gap-2 bg-refocc-orange/20 text-refocc-orange px-5 py-2 rounded-full text-xs uppercase tracking-[0.2em] font-medium mb-6">
          <Award size={14} /> Dyuthi — 10th Anniversary Initiative
        </div>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight">REFOCC Decennial <em className="italic text-refocc-orange font-normal">Scholars Award</em></h2>
        <p className="mt-6 text-lg text-refocc-cream/70 max-w-3xl mx-auto leading-relaxed">
          As REFOCC celebrates its 10th anniversary through Dyuthi, a series of meaningful community-focused programmes, the REFOCC Decennial Scholars Award stands as one of its most important educational initiatives.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <div className="grid grid-cols-2 gap-4">
          <img src={`${ASSET}/dyuthi-brochure.jpg`} alt="Dyuthi Brochure" className="w-full rounded-2xl shadow-2xl col-span-2 aspect-[16/10] object-cover" />
          <img src={`${ASSET}/dyuthi-scholars-award.jpg`} alt="REFOCC Decennial Scholars Award" className="w-full rounded-2xl shadow-2xl col-span-2 aspect-[16/10] object-cover" />
        </div>
        <div>
          <p className="text-refocc-cream/80 leading-relaxed mb-8">
            Created to honour a decade of service and carry that spirit into the future, this scholarship programme supports 10 deserving students over a period of four years, helping them continue their education with confidence, stability, and community backing.
          </p>
          <p className="text-refocc-cream/80 leading-relaxed mb-8">
            Along with the long-term scholarship support, the initiative also presents a special one-time cash award of INR 5,000 each to 40 students, extending encouragement to more young learners who are working hard to move forward despite challenges.
          </p>
          <p className="text-refocc-cream/70 leading-relaxed mb-10">
            Dyuthi reflects REFOCC's commitment to social responsibility, educational empowerment, and the belief that every student deserves a chance to rise.
          </p>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: GraduationCap, label: 'Long-term support', val: '10 students over 4 years' },
              { icon: Award, label: 'One-time award', val: '₹5,000 × 40 students' },
              { icon: BookOpen, label: 'Core focus', val: 'Education & empowerment' },
              { icon: Users, label: 'Spirit', val: 'Community responsibility' },
            ].map(b => (
              <div key={b.label} className="bg-white/10 backdrop-blur rounded-2xl p-5 border border-white/10">
                <b.icon className="text-refocc-orange mb-3" size={20} />
                <div className="font-serif text-lg text-refocc-cream">{b.val}</div>
                <div className="text-xs text-refocc-cream/60 mt-1">{b.label}</div>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/scholarship" className="inline-flex items-center gap-2 bg-refocc-orange text-white px-6 py-3 rounded-full font-medium hover:bg-[#B97651] transition-colors">Support education <ArrowRight size={16} /></Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const GalleryStrip = () => (
  <section className="py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-end mb-10">
        <div>
          <div className="text-refocc-orange uppercase tracking-[0.2em] text-xs mb-4">Moments</div>
          <h2 className="font-serif text-4xl md:text-5xl text-refocc-dark">A glimpse into our <em className="italic text-refocc-orange font-normal">days.</em></h2>
        </div>
        <Link to="/gallery" className="hidden sm:inline-flex items-center gap-2 text-refocc-dark font-medium hover:text-refocc-orange">See full gallery <ArrowUpRight size={16} /></Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {galleryImages.slice(0, 8).map((src, i) => (
          <div key={i} className={`overflow-hidden rounded-2xl bg-refocc-dark/10 ${i % 3 === 0 ? 'aspect-[3/4]' : 'aspect-square'}`}>
            <img src={src} alt={`Gallery ${i + 1}`} loading="lazy" className="w-full h-full object-cover img-hover" />
          </div>
        ))}
      </div>
    </div>
  </section>
);

const CTA = () => (
  <section className="py-24">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-refocc-dark text-refocc-cream rounded-[2rem] p-10 md:p-16 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-72 h-72 bg-refocc-orange/15 rounded-full blur-3xl"></div>
        <div className="relative grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-refocc-orange uppercase tracking-[0.2em] text-xs mb-4">Join us</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">Become part of the <em className="italic text-refocc-orange font-normal">REFOCC journey.</em></h2>
          </div>
          <div>
            <p className="text-refocc-cream/70 leading-relaxed">Volunteer with your local chapter, support a student through their education, or sponsor a community programme. Every contribution matters.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-refocc-orange text-white px-6 py-3 rounded-full font-medium hover:bg-[#B97651] transition-colors">Contact Us <ArrowRight size={16} /></Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Home = () => (
  <>
    <Hero />
    <About />
    <Pillars />
    <EventsPreview />
    <DyuthiSection />
    <GalleryStrip />
    <CTA />
  </>
);

export default Home;
